"use client";

/**
 * Live Prices on Sign-In (category, 2026-08-23): the in-place full build for a
 * stale category page (/c/list/<slug>).
 *
 * When a SIGNED-IN visitor lands on a STALE category page (auto, requirement R5),
 * or a signed-out visitor signs in via the ribbon (R4, which flips isSignedIn),
 * this SWAPS the stale list for the EXISTING live category build
 * (BroadStreamView -> POST /api/broad/resolve/stream) IN NORMAL DOCUMENT FLOW,
 * exactly like a fresh category search on /compare: the global site header (and
 * the signed-in avatar) stay visible, and there is a single page scrollbar.
 *
 * 2026-08-24 UI fix: this used to render BroadStreamView inside a
 * `fixed inset-0` overlay. That covered the global SiteHeader (the visitor
 * looked signed OUT for the whole 30-60s build) and stacked a second scrollbar
 * (the overlay's overflow-y-auto over the body's). The fix is structural: the
 * component now WRAPS the page content as children and swaps content <->
 * build view in flow, never floating above the chrome.
 *
 * On completion it reveals the freshly-built category by landing on the slug
 * the build saved under (_broad_slug(query), the /c/list key), inside ONE
 * React transition (router.replace + router.refresh + drop the build view
 * commit together), so the fresh server payload appears with no stale flash.
 *
 * The build never goes through /compare (which cannot land on /c/list and
 * would collapse a category into a single product). Signed-in only, so the
 * guarded stream carries the Clerk bearer token (bearerHeaders) and bots /
 * signed-out visitors never activate it - they get the server-rendered
 * children untouched. Inert while NEXT_PUBLIC_LIVE_PRICES_ENABLED is off.
 */

import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  useTransition,
  type ReactNode,
} from "react";

import { useRouter } from "next/navigation";
import type { Route } from "next";
import { useUser } from "@clerk/nextjs";

import { BroadStreamView } from "./BroadStreamView";
import { acquireBuildLock } from "@/lib/buildLock";
import { LIVE_PRICES_ENABLED } from "@/lib/flags";

/** Hydration-time signed-in HINT, readable synchronously long before Clerk
 *  finishes booting: Clerk's __client_uat cookie holds a unix timestamp > 0
 *  while a session exists (0 / absent when signed out). UI-cover only - the
 *  PAID stream always waits for the verified useUser() state. */
function clerkSessionHint(): boolean {
  try {
    const m = document.cookie.match(/(?:^|;\s*)__client_uat=(\d+)/);
    return !!m && Number(m[1]) > 0;
  } catch {
    return false;
  }
}


interface Props {
  /** The category query the build re-runs (detail.raw_query). */
  query: string;
  countryCode: string;
  isStale: boolean;
  /** Pricing postcode; the category pages use the AU-generic default. */
  postcode?: string;
  /** The server-rendered category page. Rendered untouched unless a signed-in
   *  visitor on a stale page activates the build (then swapped for it). */
  children: ReactNode;
}

export function CategoryLiveBuild({
  query,
  countryCode,
  isStale,
  postcode = "2000",
  children,
}: Props) {
  const { isSignedIn, isLoaded, user } = useUser();
  const router = useRouter();
  const [active, setActive] = useState(false);
  const started = useRef(false);
  const [, startTransition] = useTransition();
  // 2026-08-24 UX fix: Clerk takes ~0.5-2s to boot, and waiting for isLoaded
  // meant the STALE list flashed before the build swapped in. The cookie hint
  // covers the stale content at hydration time (in flow, header stays); Clerk's
  // verified answer then either mounts the real (paid) build or, on a stale
  // cookie for a signed-out visitor, drops the cover (page + ribbon as normal).
  const [hintCover, setHintCover] = useState(false);

  const q = (query ?? "").trim();
  const eligible = LIVE_PRICES_ENABLED && isStale && q.length > 0;

  // Before the next paint after hydration: cover a stale page when the session
  // cookie says this is (very likely) a signed-in visitor.
  useLayoutEffect(() => {
    if (eligible && clerkSessionHint()) setHintCover(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // Clerk confirmed signed OUT: the hint was stale, uncover (ribbon flow).
    if (isLoaded && !isSignedIn && hintCover) setHintCover(false);
  }, [isLoaded, isSignedIn, hintCover]);

  // 2026-09-09 safety valve (same philosophy as BUILD_LOCK_TTL_MS): a dimmed,
  // click-disabled page must never be able to wedge. If neither Clerk's
  // verdict nor the build swap has cleared the cover within 10s, drop it -
  // worst case the stale list is readable while any rebuild continues.
  useEffect(() => {
    if (!hintCover) return;
    const t = window.setTimeout(() => setHintCover(false), 10_000);
    return () => window.clearTimeout(t);
  }, [hintCover]);

  // Fire once: a signed-in visitor on a stale category page (auto R5, or R4 after
  // the ribbon signs them in and isSignedIn flips true). The build lock stops a
  // second visit inside the build window from launching a duplicate paid build.
  useEffect(() => {
    if (!eligible || !isLoaded || started.current) return;
    if (!isSignedIn) return;
    started.current = true;
    if (!acquireBuildLock(q)) {
      // A build for this category is already running (this browser): show the
      // saved page as-is; it turns fresh when the running build lands.
      setHintCover(false);
      return;
    }
    // 2026-09-09 (owner-caught grey-page bug): the dim cover's ONLY job is
    // bridging the gap until the build view swaps in - drop it the moment
    // the build activates. It used to stay true here, so when the build
    // later exited back to the children (stream error / non-broad /
    // reveal), the page rendered inside the still-dimmed, click-disabled
    // wrapper FOREVER until a manual reload. Same fix in ProductLiveBuild.
    setHintCover(false);
    setActive(true);
  }, [eligible, isLoaded, isSignedIn, q]);

  function reveal(slug: string | null) {
    // The build saved the fresh picks under `slug` (_broad_slug(query), the
    // /c/list key) BEFORE emitting `done` (stream.py saves pre-done), so the
    // re-read below is guaranteed fresh. One transition: the build view stays up
    // until the fresh server payload is ready, then both commit together.
    startTransition(() => {
      if (slug) {
        router.replace(`/c/list/${slug}` as Route);
      }
      router.refresh();
      setActive(false);
      // 2026-09-09: belt-and-braces with the activation-time clear above -
      // no exit from the build may ever land back on a dimmed page.
      setHintCover(false);
    });
  }

  if (!LIVE_PRICES_ENABLED) {
    return <>{children}</>;
  }

  // Hint phase (Clerk still booting): DIM the stale list instead of blanking it
  // (a blank frame reads as a blink). The wrapper div renders always, so the DOM
  // tree is stable across idle -> hint (only the className changes, no remount);
  // clicks are disabled while dimmed. The paid stream has NOT started yet.
  if (!active) {
    return (
      <div
        aria-busy={hintCover || undefined}
        className={
          hintCover
            ? "pointer-events-none select-none opacity-40 transition-opacity duration-300"
            : "transition-opacity duration-300"
        }
      >
        {children}
      </div>
    );
  }

  // Swap the stale content for the live build, in flow (header stays, one
  // scrollbar) - the same body a fresh category search renders on /compare.
  return (
    <BroadStreamView
      query={q}
      postcode={postcode}
      countryCode={countryCode}
      userId={user?.id ?? null}
      onComplete={reveal}
      // We are rebuilding a KNOWN category page: open on the in-flow header +
      // skeletons from the first frame, never the full-screen scene (no blink,
      // chrome untouched).
      assumeBroad
      // A non-broad classification or a stream failure must never dead-end the
      // page: drop the build view and show whatever the page already has.
      onNonBroad={() => reveal(null)}
      onError={() => reveal(null)}
    />
  );
}
